import api from './api';
import {
  getAccessToken,
  clearAuthTokens,
  getPersistedClientPortalFlags,
  mergeClientPortalFlags,
  persistClientPortalFlags,
} from './auth';
import { getEmailFromToken, getRoleFromToken } from './tokenClaims';
import { clearSession, getSession, setSession } from './sessionStore';

function parseAdminDetailsBody(data) {
  if (!data || typeof data !== 'object') return { name: '', role: '' };
  const inner = data.user ?? data.data ?? data.admin ?? data.record;
  const src = inner && typeof inner === 'object' ? { ...data, ...inner } : data;
  const role = src.role ?? '';
  return { role: String(role).trim() };
}

async function fetchClientLoginExtras() {
  try {
    const res = await api.get('/client/login-redirect');
    if (!res.data?.success) return { ok: false };
    return {
      ok: true,
      allowUserAppOnWeb: res.data.allowUserAppOnWeb,
      allowAccessWithoutPayment: res.data.allowAccessWithoutPayment,
      canAccessRobots: res.data.canAccessRobots,
      redirectRoute: res.data.redirectRoute,
    };
  } catch {
    return { ok: false };
  }
}

function applyClientPortalFlags(target, flags) {
  if (!flags) return;
  target.allowUserAppOnWeb = Boolean(flags.allowUserAppOnWeb);
  target.allowAccessWithoutPayment = Boolean(flags.allowAccessWithoutPayment);
  target.canAccessRobots = Boolean(flags.canAccessRobots);
}

/**
 * Validates the current token against the backend and hydrates in-memory session.
 * @param {object} [loginPayload] — optional fresh login response fields
 */
export async function bootstrapSession(loginPayload = null) {
  const token = getAccessToken();
  if (!token) {
    clearSession();
    return null;
  }

  if (loginPayload) {
    const portalFlags = {
      allowUserAppOnWeb: Boolean(loginPayload.allowUserAppOnWeb),
      allowAccessWithoutPayment: Boolean(loginPayload.allowAccessWithoutPayment),
      canAccessRobots: Boolean(loginPayload.canAccessRobots),
    };
    setSession({
      email: loginPayload.email || '',
      role: loginPayload.role || '',
      ...portalFlags,
      subscriptionAdmin: loginPayload.subscriptionAdmin || null,
    });
    return getSession();
  }

  const emailFromToken = getEmailFromToken(token);
  const roleFromToken = getRoleFromToken(token);

  if (!emailFromToken) {
    clearSession();
    clearAuthTokens();
    return null;
  }

  let role = roleFromToken;
  try {
    const res = await api.get(`getemail-admin-details/${encodeURIComponent(emailFromToken)}`);
    const parsed = parseAdminDetailsBody(res.data);
    if (parsed.role) role = parsed.role;
  } catch {
    if (!role) {
      clearSession();
      clearAuthTokens();
      return null;
    }
  }

  const base = {
    email: emailFromToken,
    role,
    allowUserAppOnWeb: false,
    allowAccessWithoutPayment: false,
    canAccessRobots: false,
    subscriptionAdmin: null,
  };

  const roleNorm = String(role ?? '').trim().toLowerCase();
  if (roleNorm === 'user') {
    base.role = 'user';
    const persisted = getPersistedClientPortalFlags();
    const extras = await fetchClientLoginExtras();
    if (extras.ok) {
      const merged = mergeClientPortalFlags(extras, persisted);
      applyClientPortalFlags(base, merged);
      persistClientPortalFlags(merged);
    } else {
      applyClientPortalFlags(base, persisted);
    }
  } else {
    try {
      const { subscriptionAdminApi } = await import('../subscriptionAdmin/api');
      const authInfo = await subscriptionAdminApi.authInfo();
      if (authInfo) {
        base.subscriptionAdmin = authInfo;
      }
    } catch {
      /* subscription admin module optional */
    }
  }

  setSession(base);
  return getSession();
}
